import React, { useState, useEffect } from 'react';
import { Settings, Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/services/api';

import { ReferralSettings } from './settings/ReferralSettings';
import { LoyaltySettings } from './settings/LoyaltySettings';
import { UniversityPartnerSettings, BusinessPartnerSettings } from './settings/PartnerSettings';
import { BulkDiscountSettings, CommunicationSettings, BusinessHoursSettings } from './settings/CommunicationSettings';

const DEFAULT_SETTINGS = {
  referral_program_enabled: true, referral_credits_per_use: 1, referral_discount_percent: 10,
  referrer_reward_percent: 5, max_referral_uses_per_customer: 3,
  partners_enabled: true, university_partners_enabled: true,
  university_discounts: {
    'uct': { name: 'UCT', discount_percent: 15, code: 'UCT15', enabled: true },
    'stellenbosch-university': { name: 'Stellenbosch (Maties)', discount_percent: 20, code: 'MATIE20', enabled: true },
    'uwc': { name: 'UWC', discount_percent: 15, code: 'UWC15', enabled: true },
    'cput': { name: 'CPUT', discount_percent: 15, code: 'CPUT15', enabled: true }
  },
  business_partner_discounts: {
    'oak-furniture': { name: 'Oak Furniture Store', discount_percent: 10, code: 'OAK10', enabled: true },
    'cape-properties': { name: 'Cape Properties', discount_percent: 15, code: 'CAPEMOVE15', enabled: true }
  },
  bulk_discount_enabled: true, bulk_discount_threshold: 5, bulk_discount_percent: 10,
  loyalty_program_enabled: true, loyalty_points_per_rand: 1, loyalty_points_redemption_rate: 100,
  loyalty_tier_silver: 500, loyalty_tier_gold: 1500, loyalty_tier_platinum: 3000,
  loyalty_silver_discount: 5, loyalty_gold_discount: 10, loyalty_platinum_discount: 15,
  auto_whatsapp_enabled: true, auto_email_enabled: true, whatsapp_response_interval_minutes: 5,
  business_hours_start: '07:00', business_hours_end: '19:00',
  weekend_hours_start: '08:00', weekend_hours_end: '16:00',
  webhook_api_key: ''
};

const SettingsPanel = () => {
  const [settings, setSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/api/admin/settings');
      if (response.data.success) setSettings(response.data.settings);
    } catch {
      toast.error('Failed to load settings');
      setSettings(DEFAULT_SETTINGS);
    } finally { setIsLoading(false); }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await api.put('/api/admin/settings', { settings });
      toast.success('Settings saved successfully');
    } catch { toast.error('Failed to save settings'); }
    finally { setIsSaving(false); }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#039fff]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#001A47]">System Settings</h2>
          <p className="text-gray-500">Configure features, discounts, partners, and loyalty programs</p>
        </div>
        <Button onClick={handleSave} disabled={isSaving} data-testid="save-settings-btn">
          {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save Changes
        </Button>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <ReferralSettings settings={settings} updateSetting={updateSetting} />
        <LoyaltySettings settings={settings} updateSetting={updateSetting} />
        <UniversityPartnerSettings settings={settings} updateSetting={updateSetting} />
        <BusinessPartnerSettings settings={settings} updateSetting={updateSetting} />
        <BulkDiscountSettings settings={settings} updateSetting={updateSetting} />
        <CommunicationSettings settings={settings} updateSetting={updateSetting} />
        <BusinessHoursSettings settings={settings} updateSetting={updateSetting} />
      </div>
    </div>
  );
};

export default SettingsPanel;
